/** Shared helpers for downloading generated media (images/videos) to disk. */
import type { Job } from '@volt/shared';

/**
 * What to show inline for result `index`: the small server preview when there is one,
 * the original otherwise. Full-screen views and downloads always use the original.
 */
export function jobPreviewUrl(job: Job, index: number, original: string): string {
  return job.previewUrls?.[index] ?? original;
}

/** Path part of a URL, so query strings (signatures, tokens with dots) never look like extensions. */
function urlPath(url: string): string {
  try {
    return new URL(url, 'https://volt.invalid').pathname;
  } catch {
    return url.split('?')[0] ?? url;
  }
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v', 'ogg', 'ogv'];

/** Trigger a browser download of a URL (http/https or data:) with a suggested filename. */
export function downloadUrl(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Derive a file extension from a URL or data URL for the given media kind. */
export function getExtensionFromUrl(url: string, kind: 'image' | 'video' = 'image'): string {
  if (kind === 'image') {
    if (url.startsWith('data:image/png')) return 'png';
    if (url.startsWith('data:image/webp')) return 'webp';
    if (url.startsWith('data:image/gif')) return 'gif';
  } else {
    if (url.startsWith('data:video/mp4')) return 'mp4';
    if (url.startsWith('data:video/webm')) return 'webm';
  }
  const ext = urlPath(url).split('.').pop()?.toLowerCase();
  const allowed = kind === 'image' ? IMAGE_EXTENSIONS : VIDEO_EXTENSIONS;
  if (ext && allowed.includes(ext)) return ext;
  return kind === 'image' ? 'jpg' : 'mp4';
}

/** Heuristic: does this result URL point at a video file (vs. an image)? */
export function isVideoUrl(url: string): boolean {
  if (url.startsWith('data:video/')) return true;
  if (url.startsWith('data:image/')) return false;
  const ext = urlPath(url).split('.').pop()?.toLowerCase();
  return !!ext && VIDEO_EXTENSIONS.includes(ext);
}
